import React, {useId, useRef, useState} from 'react';
import './members.css';
import { Link } from 'react-router-dom';
import { ArrowRight, Loader2, Menu, X } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext.tsx';
export const primary = 'inline-flex items-center justify-center gap-2 rounded-xl min-h-[40px] bg-sminy-primary px-5 py-2 text-sm font-medium text-white hover:bg-[#0057DD] transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sminy-primary';
export function MembersShell({
  children,
  action,
  mobileNavigation,
  fullWidth = false
}: React.PropsWithChildren<{
  action?: React.ReactNode;
  mobileNavigation?: React.ReactNode;
  fullWidth?: boolean;
}>) {
  const {
    isDark
  } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuId = useId();
  const toggleRef = useRef<HTMLButtonElement>(null);
  const links = <><Link className="text-sm members-link" to="/members/parceiros">Encontrar parceiro</Link>{action || <Link className="text-sm members-link inline-flex items-center gap-2" to="/members/login">Acessar minha conta <ArrowRight aria-hidden="true" size={16} /></Link>}</>;
  return <div className={`min-h-screen pb-[max(2rem,env(safe-area-inset-bottom))] members-theme ${isDark ? 'members-theme-dark' : ''}`}>

    <header className={`border-b px-4 py-5 border-[var(--members-border)]`}>
      <div className="mx-auto flex max-w-[1120px] flex-wrap items-center justify-between gap-4">
        <Link to={window.location.hostname === 'members.sminy.ai' ? '/' : '/members'} className="inline-flex min-h-[40px] shrink-0 items-center" aria-label="Sminy Members — início">
          {/* Compensa a margem transparente à direita dos arquivos oficiais. */}
          <img src={isDark ? '/logo-sminy-white.png' : '/logo-sminy.png'} alt="Sminy" width={734} height={141} className="-mr-2 h-6 w-auto" />
          <span className="text-sm font-normal">Members</span>
        </Link>
        <nav className="hidden items-center gap-5 md:flex" aria-label="Members">{links}</nav>
        <button ref={toggleRef} type="button" className="members-link flex h-11 w-11 items-center justify-center rounded-xl border border-[var(--members-border)] md:hidden" aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'} aria-expanded={menuOpen} aria-controls={menuId} onClick={() => setMenuOpen(open => !open)}>{menuOpen ? <X aria-hidden="true" size={22} /> : <Menu aria-hidden="true" size={22} />}</button>
        {menuOpen && <nav id={menuId} aria-label="Menu mobile Members" className="members-mobile-menu w-full border-t border-[var(--members-border)] pt-4 md:hidden" onKeyDown={event => {if (event.key === 'Escape') {setMenuOpen(false); toggleRef.current?.focus();}}} onClick={event => {if ((event.target as HTMLElement).closest('a, button')) {setMenuOpen(false); toggleRef.current?.focus();}}}>
          {mobileNavigation}<div className="flex flex-col items-stretch gap-2">{links}</div>
        </nav>}
      </div>
    </header>

    <main className={fullWidth ? 'members-home' : 'mx-auto max-w-[1120px] px-4 py-8 sm:py-12'}>{children}</main>
  </div>;
}
export function Card({
  children,
  className = ''
}: React.PropsWithChildren<{
  className?: string;
}>) {
  return <section className={`rounded-2xl border p-5 sm:p-6 shadow-sm members-card ${className}`}>{children}</section>;
}
export function Field({
  label,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & {
  label: string;
}) {
  return <label className="block space-y-2 text-sm">
    <span className="font-medium">{label}{props.required ? ' *' : ''}</span>
    <input {...props} className="members-input w-full min-w-0 text-sm" />
  </label>;
}
export function Notice({
  children,
  error = false
}: React.PropsWithChildren<{
  error?: boolean;
}>) {
  return <p role={error ? 'alert' : 'status'} className={`rounded-xl border p-4 text-sm ${error ? 'border-red-400/40 bg-red-400/10' : 'border-sminy-primary/30 bg-sminy-primary/10'}`}>{children}</p>;
}
export function Loading() {
  return <div role="status" className="flex items-center justify-center gap-3 py-16">
    <Loader2 className="animate-spin members-link" />Carregando...</div>;
}
export function Pagination({
  offset,
  total,
  onChange,
  busy = false,
  pageSize = 25
}: {
  offset: number;
  total: number;
  onChange: (offset: number) => void;
  busy?: boolean;
  pageSize?: number;
}) {
  if (total <= pageSize) return null;
  return <nav aria-label="Paginação" className="flex flex-wrap items-center justify-between gap-3 pt-5 text-sm">
    <button disabled={busy || offset === 0} onClick={() => onChange(Math.max(0, offset - pageSize))} className="rounded-xl border px-4 py-2 disabled:opacity-40">Anterior</button>
    <span>Página {offset / pageSize + 1} de {Math.ceil(total / pageSize)}</span>
    <button disabled={busy || offset + pageSize >= total} onClick={() => onChange(offset + pageSize)} className="rounded-xl border px-4 py-2 disabled:opacity-40">Próxima</button>
  </nav>;
}
